import Link from 'next/link'

type NavItem = {
  href: string
  title: string
}

type PreviousAndNextProps = {
  previous?: NavItem
  next?: NavItem
  /** View All の遷移先（一覧ページ） */
  allHref: string
}

const labelClassName =
  'flex items-center gap-1 text-sm font-bold leading-compact text-ink-subtle'
// SP ではタイトルを出さない（Figma: 5266:57752）。line-clamp が display を持つので md: で出し分ける
const titleClassName =
  'hidden text-sm font-bold leading-compact text-ink group-hover/nav:underline md:line-clamp-2'

/**
 * 記事下のナビ（Prev｜View All｜Next）。Figma: nav/article。
 * 上下の余白は持たないので、置く側で指定する（Figma: 1240 幅なら 80、740 幅なら 32）。
 * SP（Figma: 5266:57752）はタイトルと「View All」の文字を省き、Prev / グリッド / Next を
 * 縦中央でそろえて 1 行に並べる。区切り線は行の高さ（グリッドの 40px）に追従する。
 * タイトルと View All は SP でも読み上げられるよう aria-label / sr-only で残す。
 */
export function PreviousAndNext({
  previous,
  next,
  allHref
}: PreviousAndNextProps) {
  return (
    <nav
      aria-label="前後の記事"
      className="flex items-center md:items-start md:gap-8"
    >
      <div className="min-w-0 flex-1">
        {previous ? (
          <Link
            href={previous.href}
            aria-label={`前の記事：${previous.title}`}
            className="group/nav flex flex-col gap-3"
          >
            <span className={labelClassName}>
              <span aria-hidden className="icon icon-chevron-left size-6" />
              Prev
            </span>
            <span className={titleClassName}>{previous.title}</span>
          </Link>
        ) : null}
      </div>
      <span aria-hidden className="divider-dashed" />
      <Link
        href={allHref}
        className="flex w-32 shrink-0 flex-col items-center gap-3"
      >
        <span className="sr-only text-sm font-bold leading-compact text-ink-subtle md:not-sr-only">
          View All
        </span>
        <span aria-hidden className="icon icon-grid size-10 text-ink" />
      </Link>
      <span aria-hidden className="divider-dashed" />
      <div className="min-w-0 flex-1">
        {next ? (
          <Link
            href={next.href}
            aria-label={`次の記事：${next.title}`}
            className="group/nav flex flex-col gap-3 text-right"
          >
            <span className={`${labelClassName} justify-end`}>
              Next
              <span aria-hidden className="icon icon-chevron-right size-6" />
            </span>
            <span className={titleClassName}>{next.title}</span>
          </Link>
        ) : null}
      </div>
    </nav>
  )
}

/**
 * 記事の本文カラム（740 / 880 幅）の末尾に置く版。Figma: sec/pager。
 * PreviousAndNext を bg-surface-subtle / p-8 の面に載せる。
 */
export function PreviousAndNextPanel(props: PreviousAndNextProps) {
  return (
    <div className="rounded-xl bg-surface-subtle p-8">
      <PreviousAndNext {...props} />
    </div>
  )
}
